/* eslint-disable react/jsx-key */
import React, {
  createRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useHistory } from "react-router-dom";
import { usePagination, useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";
import { withTransientProps } from "core/utils/transientProps";

import {
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";

import ProgressIndicatorOverlay from "components/ProgressIndicatorOverlay";
import { Pagination } from "components/SharedTable/Pagination";
import { AddCaseButton } from "components/CaseManagement/AddCaseButton";
import { DownDerateTrackerExportButton } from "components/DownDerateTrackerTable/DownDerateTrackerExportButton";
import { TablePaginationActions } from "components/SharedTable/TablePaginationActions";

import useDumbPageTitle from "hooks/useDumbPageTitle";
import { useExportDdt } from "hooks/useExport";
import { useTrackPageView } from "hooks/useTrackPageView";
import { useDownDerateTrackerData } from "contexts/DownDerateDataContext";

import {
  CONSTANT_HIDDEN_COLUMNS,
  DOWN_DERATE_TABLE_COLUMNS
} from "components/DownDerateTrackerTable/DownDerateTrackerTableColumns";

import { styled } from "@mui/material/styles";
import { DownDerateTableFilters } from "components/DownDerateTrackerTable/DownDerateTableFilters";
import { useLoadCounter } from "hooks/useLoadCounter";

const PAGE_TITLE = "Down/Derated Tracker";

function DownDerateTrackerPage() {
  useDumbPageTitle(PAGE_TITLE);
  useTrackPageView(PAGE_TITLE);

  const [mousePos, setMousePos] = useState({ mouseX: null, mouseY: null });
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const {
    tableState,
    setTableState,
    casesLoading,
    refetchDownDerateTableData
  } = useDownDerateTrackerData();
  const tableLoadCount = useLoadCounter(casesLoading);

  const history = useHistory();
  const tableRef = createRef();

  const [exportCases, { loading: exportLoading }] = useExportDdt();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    visibleColumns
  } = useTable(
    {
      columns: useMemo(() => DOWN_DERATE_TABLE_COLUMNS, []),
      data: useMemo(() => tableState.cases, [tableState.cases]),
      hiddenColumns: useMemo(() => CONSTANT_HIDDEN_COLUMNS, []),
      manualSortBy: true,
      manualFilters: true,
      manualPagination: true,
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      useControlledState: state =>
        useMemo(
          () => ({
            ...state,
            sortBy: tableState.sortBy,
            hiddenColumns: CONSTANT_HIDDEN_COLUMNS
          }),
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [tableState]
        )
    },
    useSortBy,
    usePagination
  );

  useEffect(() => refetchDownDerateTableData(), [refetchDownDerateTableData]);

  const setFilters = useCallback((title, values) => {
    setTableState(state => ({
      ...state,
      filters: { ...state.filters, [title]: values },
      pageIndex: 0
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = useCallback(() => {
    setTableState(state => ({
      ...state,
      filters: {},
      pageIndex: 0
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSortBy = useCallback(
    ({ id, disableSortBy, isSortedDesc }, event) => {
      if (disableSortBy) return;

      let desc = false;
      if (isSortedDesc !== undefined) {
        desc = !isSortedDesc;
      }

      if (event.shiftKey) {
        // To enable multi column sorting, hold shift key down to add clicked column to existing sort.
        setTableState(state => {
          const existingSortBy = state.sortBy || []; // Get the current sorting state of the table.

          const sortBy = existingSortBy.filter(sort => sort.id !== id); // Filter out columns that are already sorted.
          sortBy.push({ id, desc }); // Add the new sort to the start of sortBy array.

          return {
            ...state,
            sortBy,
            pageIndex: 0
          };
        });
      } else {
        // If no modifier key held down, clear the existing sort and sort only by the clicked column.
        setTableState(state => {
          return {
            ...state,
            sortBy: [{ id, desc }],
            pageIndex: 0
          };
        });
      }

      refetchDownDerateTableData();
    },
    [setTableState, refetchDownDerateTableData]
  );

  const handleChangePage = useCallback((_event, pageIndex) => {
    setTableState(state => ({
      ...state,
      pageIndex
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeRowsPerPage = useCallback(event => {
    setTableState(state => ({
      ...state,
      rowsPerPage: Number(event.target.value)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRowClicked = useCallback(
    (selectedCaseId, title) => {
      if (!selectedCaseId) return;
      if (title === "Expected RTS") return;

      history.push({
        pathname: `case-management/${selectedCaseId}`,
        state: {
          downDerateTrackerSource: true
        }
      });
    },
    [history]
  );

  const handelOpenInNewTab = useCallback(() => {
    setMousePos({ mouseX: null, mouseY: null });

    if (selectedCaseId) {
      const handle = window.open(
        history.createHref({
          pathname: `case-management/${selectedCaseId}`
        }),
        "_blank"
      );
      handle.blur();
      window.focus();
    }
  }, [history, selectedCaseId]);

  const onRowConextMenu = useCallback((caseId, event) => {
    event.preventDefault();
    setSelectedCaseId(caseId);
    setMousePos({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
  }, []);

  const handleMenuClosed = useCallback(() => {
    setMousePos({ mouseX: null, mouseY: null });
    setSelectedCaseId(null);
  }, []);

  const getAnchorPosition = useCallback(() => {
    return mousePos.mouseY !== null && mousePos.mouseX !== null
      ? { top: mousePos.mouseY, left: mousePos.mouseX }
      : undefined;
  }, [mousePos.mouseX, mousePos.mouseY]);

  const exportTableCases = useCallback(() => {
    exportCases(tableState.filters);
  }, [exportCases, tableState.filters]);

  const RenderHeaders = useCallback(
    headerGroups => {
      return headerGroups.map(headerGroup => (
        <TableRow {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map((column, i) => (
            <TableHeaderCell
              data-col={i + 1}
              {...column.getHeaderProps()}
              onClick={event => setSortBy(column, event)}
            >
              {column.render("Header")}
            </TableHeaderCell>
          ))}
        </TableRow>
      ));
    },
    [setSortBy]
  );

  const RenderRows = useCallback(
    page => {
      if (page?.length > 0) {
        return page.map(row => {
          prepareRow(row);

          return (
            <TableBodyRow
              {...row.getRowProps()}
              $rowHasCase={Boolean(row.original.case)}
              onContextMenu={event => onRowConextMenu(row.original.case, event)}
            >
              {row.cells.map(cell => (
                <TableBodyCell
                  {...cell.getCellProps()}
                  onClick={_event =>
                    onRowClicked(row.original.case, cell.column.title)
                  }
                >
                  {cell.render("Cell")}
                </TableBodyCell>
              ))}
            </TableBodyRow>
          );
        });
      } else {
        return (
          <TableRow>
            <TableBodyCell colSpan={headerGroups[0]?.headers?.length || 1}>
              <TablePlaceHolder>No data</TablePlaceHolder>
            </TableBodyCell>
          </TableRow>
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prepareRow]
  );

  const LoadingCell = () => {
    return casesLoading ? (
      <TableRow>
        <TableLoadingCell colSpan={visibleColumns.length}>
          <LinearProgress />
        </TableLoadingCell>
      </TableRow>
    ) : (
      <TableRow>
        <TableLoadingCell colSpan={visibleColumns.length} />
      </TableRow>
    );
  };

  return (
    <>
      <DownDerateTableFilters
        filters={tableState.filters}
        setFilters={setFilters}
        clearFilters={clearFilters}
      />

      <Menu
        open={mousePos.mouseY !== null}
        onClose={handleMenuClosed}
        anchorReference="anchorPosition"
        anchorPosition={getAnchorPosition()}
        keepMounted
      >
        <MenuItem onClick={handelOpenInNewTab}>Open in new tab</MenuItem>
      </Menu>

      <StyledPaper>
        <ProgressIndicatorOverlay loading={exportLoading} />

        <TableHeader>Results</TableHeader>
        <CaseTableSettings
          exportCases={exportTableCases}
          exportLoading={exportLoading}
        />
        <TableWrapper>
          <TableContainer>
            <SiteTableScroll>
              <PerfectScrollbar>
                <Table
                  data-qa="DownDerateTracker-Table-Cases"
                  stickyHeader
                  ref={tableRef}
                  {...getTableProps()}
                >
                  <TableHead>
                    {RenderHeaders(headerGroups)}
                    <LoadingCell />
                  </TableHead>
                  <TableBody
                    data-qa_loading={casesLoading}
                    data-qa_load_count={tableLoadCount}
                    {...getTableBodyProps()}
                  >
                    {RenderRows(page)}
                  </TableBody>
                </Table>
              </PerfectScrollbar>
            </SiteTableScroll>
          </TableContainer>
          <Pagination
            rowCount={rows.length}
            casesCount={tableState.casesCount}
            pageSize={tableState.rowsPerPage}
            pageIndex={tableState.pageIndex}
            handlePageChange={handleChangePage}
            handleRowsPerPageChange={handleChangeRowsPerPage}
            TablePaginationActions={TablePaginationActions}
          />
        </TableWrapper>
      </StyledPaper>
    </>
  );
}

DownDerateTrackerPage.propTypes = {};

const TableWrapper = styled("div")`
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 4px;
`;

const SiteTableScroll = styled(Paper)`
  // 'PerfectScrollbar' must have 'height'
  display: block;

  // Height change follow Filtering grid
  height: calc(100vh - 475px);
  min-height: 500px;
  @media (max-width: 1900px) {
    height: calc(100vh - 520px);
  }
  @media (max-width: 1200px) {
    height: calc(100vh - 690px);
  }
  @media (max-width: 960px) {
    height: calc(100vh - 520px);
  }
  @media (max-width: 860px) {
    height: calc(100vh - 690px);
  }
`;

const StyledPaper = styled(Paper)`
  position: relative;
  width: 100%;
  padding: 16px;
`;

const TableHeader = styled(Typography)`
  font-size: 18px;
  color: #555555;
`;

const TableHeaderCell = styled(TableCell)`
  padding: 10px 0 10px 10px;
  font-weight: bold;
  text-align: left;
  vertical-align: top;
  background-color: #ffffff;
`;

const TableBodyRow = styled(TableRow, withTransientProps)`
  height: 60px;
  ${({ $rowHasCase }) =>
    $rowHasCase
      ? `&:hover {
        background-color: rgba(76, 175, 80, 0.5);
        cursor: pointer;
      }`
      : ""}
`;

const TableBodyCell = styled(TableCell)`
  width: 1%;
  padding: 10px 0 10px 10px;
`;

const TableLoadingCell = styled(TableCell)`
  padding: 0;
  height: 5px;
  z-index: 1;
`;

const TablePlaceHolder = styled("div")`
  text-align: center;
`;

const CaseTableSettings = memo(function CaseTableSettings(props) {
  const { exportCases, exportLoading } = props;

  return (
    <SettingsContainer>
      <ButtonsContainer>
        <AddCaseButton
          data-qa="DDT-Button-Add-Case"
          downDerateTrackerSource={true}
        />
        <DownDerateTrackerExportButton
          onClick={() => exportCases({})}
          disabled={exportLoading}
        />
      </ButtonsContainer>
    </SettingsContainer>
  );
});

CaseTableSettings.propTypes = {
  exportCases: PropTypes.func.isRequired,
  exportLoading: PropTypes.bool.isRequired
};

const SettingsContainer = styled("div")`
  display: flex;
  padding: 5px 0 10px 0;
`;

const ButtonsContainer = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;
  row-gap: 5px;
  column-gap: 10px;
`;

export default memo(DownDerateTrackerPage);
